import { DateTime } from 'luxon';

import { EnergyCostStoreModel } from '@domain/multi-site-dashboard/multi-site-dashboard.models';

import { generateColorPaletteWithReference } from '@shared/utils/color-utils';

import { EnergyCostRange } from '../../../enums/energy-cost-range.enum';
import {
  EnergyCostOverviewChartDatum,
  EnergyCostOverviewMovablePanelViewModel,
  EnergyCostOverviewSiteViewModel,
} from '../energy-cost-overview-movable-panel.view-model';

const ISO_DATE_FORMAT = 'yyyy-MM-dd';
const MAXIMUM_INDIVIDUAL_SITES = 5;
const AGGREGATE_ENERGY_ID = '__aggregateEnergy';

// Optimo brand blue; per-site colours are hues rotated around it (matches the meters-chart palette).
const ENERGY_COST_PALETTE_REFERENCE_COLOR = '#021CF5';

export const buildEnergyCostOverviewMovablePanelViewModel = (
  energyCost: EnergyCostStoreModel,
  range: EnergyCostRange
): EnergyCostOverviewMovablePanelViewModel => {
  const siteEntries = Object.entries(energyCost.siteNames);
  const isEnergyAggregated = siteEntries.length > MAXIMUM_INDIVIDUAL_SITES;
  const colors = generateColorPaletteWithReference(isEnergyAggregated ? 1 : siteEntries.length, ENERGY_COST_PALETTE_REFERENCE_COLOR);

  const sites: EnergyCostOverviewSiteViewModel[] = isEnergyAggregated
    ? [{ id: AGGREGATE_ENERGY_ID, name: '', color: colors[0] }]
    : siteEntries.map(([id, name], idx) => ({
        id,
        name,
        color: colors[idx],
      }));
  const weatherByDateMillis: Record<number, EnergyCostStoreModel['graphData'][number]['weather']> = {};

  const chartData: EnergyCostOverviewChartDatum[] = energyCost.graphData.map((unit) => {
    const dateMillis = DateTime.fromFormat(unit.date, ISO_DATE_FORMAT).toMillis();
    weatherByDateMillis[dateMillis] = unit.weather;
    const datum: EnergyCostOverviewChartDatum = {
      dateMillis,
      cost: unit.cost,
    };
    if (isEnergyAggregated) {
      datum[AGGREGATE_ENERGY_ID] = unit.siteData.reduce((sum, siteData) => sum + siteData.energyKwh, 0);
    } else {
      sites.forEach((site) => {
        datum[site.id] = unit.siteData.find((siteData) => siteData.siteId === site.id)?.energyKwh ?? 0;
      });
    }

    return datum;
  });

  return { range, sites, isEnergyAggregated, currency: energyCost.graphData[0]?.costCurrency, chartData, weatherByDateMillis };
};
